"use client";

import { useEffect, useRef } from "react";
import Image from "next/image";

// Depth layers, back → front.
//   scroll = how much it lags the page scroll (bigger = further away)
//   mouse  = how much it drifts with the cursor (bigger = nearer)
type Layer = {
  src: string;
  scroll: number;
  mouse: number;
  anim?: string;
  fit?: string;
  place?: boolean;
  /** Mountain layers: tile horizontally at their natural proportions instead
   *  of being stretched, so a wide screen simply shows more ridge. */
  tile?: boolean;
};

// Colored Dilijan landmark strip (same for day & night). Rendered small and
// pinned bottom-right. Currently hidden — add PLACES back to the layer arrays
// below to re-enable it.
// const PLACES: Layer = {
//   src: "/images/hero-places.svg",
//   scroll: 0.05,
//   mouse: 0.8,
//   place: true,
// };

const NIGHT_LAYERS: Layer[] = [
  { src: "/images/hero-sky.svg", scroll: 0.1, mouse: 0.3, anim: "animate-hero-zoom" },
  { src: "/images/hero-back.svg", scroll: 0.06, mouse: 0.6, anim: "hero-float-back", tile: true },
  { src: "/images/hero-front.svg", scroll: 0.02, mouse: 1, anim: "hero-float-front", tile: true },
];

const DAY_LAYERS: Layer[] = [
  { src: "/images/hero-sky-day.svg", scroll: 0.1, mouse: 0.3, anim: "animate-hero-zoom" },
  { src: "/images/hero-back-day.svg", scroll: 0.06, mouse: 0.6, anim: "hero-float-back", tile: true },
  { src: "/images/hero-front-day.svg", scroll: 0.02, mouse: 1, anim: "hero-float-front", tile: true },
];

const MOUSE_AMP = 32; // px of cursor drift at the edges

export function HeroParallax({
  night = false,
  placesVersion,
  showCar = true,
}: {
  night?: boolean;
  placesVersion?: string;
  /** Admin toggle (Configuration -> Common): the little jeep on the hero. */
  showCar?: boolean;
}) {
  const root = useRef<HTMLDivElement>(null);
  const LAYERS = night ? NIGHT_LAYERS : DAY_LAYERS;

  useEffect(() => {
    const el = root.current;
    if (!el) return;

    const layers = Array.from(
      el.querySelectorAll<HTMLElement>("[data-scroll]"),
    );
    let raf = 0;
    let mx = 0;

    const apply = () => {
      const y = window.scrollY;
      for (const l of layers) {
        const s = Number(l.dataset.scroll);
        const m = Number(l.dataset.mouse);
        // Horizontal drift from the cursor; vertical only from scroll. The
        // mountain layers are bottom-anchored, so vertical mouse drift would
        // lift them and expose the layers behind at the bottom.
        const tx = (mx * MOUSE_AMP * m).toFixed(1);
        const ty = (y * s).toFixed(1);
        l.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
      }
      raf = 0;
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(apply);
    };

    const onMove = (e: PointerEvent) => {
      mx = e.clientX / window.innerWidth - 0.5;
      schedule();
    };

    apply();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("pointermove", onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={root}
      aria-hidden
      className="absolute inset-0 -z-10 overflow-hidden"
    >
      {LAYERS.map((l) =>
        l.place ? (
          <div
            key={l.src}
            data-scroll={l.scroll}
            data-mouse={l.mouse}
            className="absolute inset-0 will-change-transform"
          >
            {/* Bottom-right with a margin from the edges; gentle zoom. */}
            <div className="hero-zoom-soft absolute bottom-6 right-4 aspect-[2814/408] w-[60%] sm:bottom-10 sm:right-10 sm:w-[53%]">
              <Image
                src={placesVersion ? `${l.src}?v=${placesVersion}` : l.src}
                alt=""
                fill
                priority
                unoptimized
                sizes="60vw"
                className="object-contain object-bottom opacity-75"
              />
            </div>
          </div>
        ) : (
          // The box is 64px wider than the hero on each side: that margin
          // absorbs the cursor drift (±32px) and the sway (±18px) so no edge
          // is ever exposed — previously a scale(1.25) did that job, which
          // zoomed the sky and back mountains out of frame on wide screens.
          <div
            key={l.src}
            data-scroll={l.scroll}
            data-mouse={l.mouse}
            className="absolute -left-16 -right-16 inset-y-0 will-change-transform"
          >
            <div className={`relative h-full w-full ${l.anim ?? ""}`}>
              {l.tile ? (
                // Mountains: repeated horizontally at their true proportions
                // (never stretched) and pinned to the bottom, so a wider screen
                // shows MORE ridge rather than a distorted one. The SVG edges
                // are authored to meet, so the repeat is seamless.
                <div
                  className="h-full w-full"
                  style={{
                    backgroundImage: `url(${l.src})`,
                    backgroundRepeat: "repeat-x",
                    backgroundPosition: "bottom center",
                    backgroundSize: "auto 100%",
                  }}
                />
              ) : (
                // Sky: keeps its aspect (a stretched moon/sun would read as an
                // ellipse) and is anchored to the TOP — the part it crops at
                // the bottom is hidden behind the mountains anyway.
                <Image
                  src={l.src}
                  alt=""
                  fill
                  priority
                  sizes="100vw"
                  className={l.fit ?? "object-cover object-top"}
                />
              )}
            </div>
          </div>
        ),
      )}

      {/* A little red jeep crossing the foreground, in front of every layer
          (rendered last) but still behind the hero text. */}
      {showCar && (
        <div className="hero-drive pointer-events-none absolute bottom-[2.5%] left-0">
          <div className="hero-bump">
            <Jeep night={night} />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Side-view 4x4, facing right (its direction of travel): short nose with the
 * headlight and bumper on the right, spare wheel on the tailgate at the left,
 * roof rack on top. The wheels spin clockwise so it never reads as reversing.
 */
function Jeep({ night = false }: { night?: boolean }) {
  // Night: darker paint (moonlit metal reads much deeper than daylight red),
  // dark glass, and the headlight actually switched on.
  const c = night
    ? {
        body: "#8f1d1d",
        cabin: "#6b1414",
        glass: "#1e293b",
        glassOpacity: 0.95,
        rack: "#1f2937",
        bumper: "#6b7280",
        headlight: "#fffbe6",
        taillight: "#ef4444",
        shade: "#450a0a",
      }
    : {
        body: "#dc2626",
        cabin: "#b91c1c",
        glass: "#dbeafe",
        glassOpacity: 0.92,
        rack: "#374151",
        bumper: "#d1d5db",
        headlight: "#fde68a",
        taillight: "#fca5a5",
        shade: "#7f1d1d",
      };

  const wheel = (cx: number, cy: number) => (
    <>
      <circle cx={cx} cy={cy} r="6.4" fill="#111827" />
      <circle cx={cx} cy={cy} r="4.4" fill="#1f2937" />
      <g className="hero-wheel" style={{ transformOrigin: `${cx}px ${cy}px` }}>
        <circle cx={cx} cy={cy} r="2.6" fill="#9ca3af" />
        <rect
          x={cx - 0.8}
          y={cy - 4.2}
          width="1.6"
          height="8.4"
          rx="0.8"
          fill="#9ca3af"
          opacity="0.85"
        />
        <rect
          x={cx - 4.2}
          y={cy - 0.8}
          width="8.4"
          height="1.6"
          rx="0.8"
          fill="#9ca3af"
          opacity="0.85"
        />
      </g>
    </>
  );

  return (
    // The viewBox starts above 0 to leave headroom for the flag; the jeep
    // itself keeps its size and stays on the ground.
    <svg
      viewBox="0 -12 110 46"
      className="h-auto w-[86px] drop-shadow-[0_2px_3px_rgba(0,0,0,0.45)] sm:w-[122px]"
      aria-hidden
    >
      {/* Headlight beam — night only. The viewBox is wider than the jeep so the
          cone has somewhere to fall; the jeep itself keeps its size because the
          CSS width grew by the same factor. */}
      {night && (
        <>
          <defs>
            <linearGradient id="jeepBeam" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#fef3c7" stopOpacity="0.55" />
              <stop offset="0.55" stopColor="#fde68a" stopOpacity="0.18" />
              <stop offset="1" stopColor="#fde68a" stopOpacity="0" />
            </linearGradient>
            <radialGradient id="jeepGlow">
              <stop offset="0" stopColor="#fff7cc" stopOpacity="0.85" />
              <stop offset="1" stopColor="#fff7cc" stopOpacity="0" />
            </radialGradient>
          </defs>
          <path d="M66 15.4 L107 6.5 L107 25.5 L66 18.6 Z" fill="url(#jeepBeam)" />
          <circle cx="66" cy="17" r="8" fill="url(#jeepGlow)" />
        </>
      )}
      {/* Armenian flag on a roof-rack pole, trailing back in the headwind */}
      <rect x="21.2" y="-10.5" width="1.2" height="15" rx="0.6" fill="#4b5563" />
      <g className="hero-flag" style={{ transformOrigin: "21.2px -6px" }}>
        <rect x="10.4" y="-9.6" width="10.8" height="2.4" fill="#D90012" />
        <rect x="10.4" y="-7.2" width="10.8" height="2.4" fill="#0033A0" />
        <rect x="10.4" y="-4.8" width="10.8" height="2.4" fill="#F2A800" />
      </g>

      {/* spare wheel on the tailgate (rear = left) */}
      <circle cx="7" cy="18.5" r="4.6" fill="#111827" />
      <circle cx="7" cy="18.5" r="2" fill="#6b7280" />

      {/* roof rack */}
      <rect x="16" y="3.6" width="27" height="1.8" rx="0.9" fill={c.rack} />

      {/* cabin: upright windscreen raked slightly forward at the front */}
      <path
        d="M15.5 14 L15.5 6.6 Q15.5 5.4 16.8 5.4 L43 5.4 Q44.6 5.4 45.4 6.6 L49.5 12.8 L49.5 14 Z"
        fill={c.cabin}
      />
      <rect x="18" y="7.4" width="11.5" height="5.4" rx="0.8" fill={c.glass} opacity={c.glassOpacity} />
      <path d="M31.5 7.4 L42.6 7.4 L46.4 12.8 L31.5 12.8 Z" fill={c.glass} opacity={c.glassOpacity} />

      {/* body tub + short bonnet */}
      <path
        d="M9 13.6 L64 13.6 Q66.6 13.6 66.6 16 L66.6 22 Q66.6 23.6 65 23.6 L10.4 23.6 Q9 23.6 9 22 Z"
        fill={c.body}
      />
      <rect x="11" y="18" width="52" height="1.2" fill={c.shade} opacity="0.45" />

      {/* wheel arches */}
      <path d="M13 23.6 A7 7 0 0 1 27 23.6 Z" fill={c.shade} opacity="0.35" />
      <path d="M47 23.6 A7 7 0 0 1 61 23.6 Z" fill={c.shade} opacity="0.35" />

      {/* front: headlight, grille bar, bumper (right = direction of travel) */}
      <rect x="62.6" y="15" width="4" height="3.2" rx="1.2" fill={c.headlight} />
      <rect x="65" y="19.6" width="4.6" height="3.6" rx="1" fill={c.bumper} />
      {/* rear tail light */}
      <rect x="9.4" y="15" width="2.6" height="2.6" rx="0.8" fill={c.taillight} />

      {wheel(20, 24)}
      {wheel(54, 24)}
    </svg>
  );
}
